<template>
  <!-- Floating phone call UI — persists across route navigation -->
  <transition name="floater-slide">
    <div
      v-if="state !== 'idle' || crossTabContext"
      data-private
      class="fixed bottom-6 right-6 z-[9999]"
    >
      <!-- Cross-tab: call active in another tab -->
      <div
        v-if="crossTabContext"
        class="flex items-center gap-2 bg-neutral-900 text-white rounded-full px-4 py-2 shadow-lg cursor-pointer select-none"
        title="Click to switch to call tab"
        @click="focusCallTab"
      >
        <span class="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        <span class="text-sm font-medium">Call active in another tab</span>
        <i class="fa-solid fa-arrow-up-right-from-square text-xs text-gray-400" />
      </div>

      <!-- Minimized pill -->
      <div
        v-else-if="minimized"
        class="flex items-center gap-3 bg-neutral-900 text-white rounded-2xl px-5 py-3 shadow-lg cursor-pointer select-none"
        @click="minimized = false"
      >
        <FontAwesomeIcon :icon="['fas', 'phone']" class="text-green-400 text-sm" />
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium">On Call</span>
          <span class="text-sm font-semibold tabular-nums">{{ formattedDuration }}</span>
        </div>
        <FontAwesomeIcon :icon="['fas', 'chevron-up']" class="text-gray-400 text-xs" />
      </div>

      <!-- Expanded card -->
      <div
        v-else
        class="bg-white rounded-xl shadow-lg border border-gray-200 w-80 overflow-hidden"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 bg-neutral-900 text-white">
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-phone text-sm" />
            <span class="text-sm font-semibold">{{ headerLabel }}</span>
          </div>
          <button
            v-if="state === 'active'"
            class="w-7 h-7 flex items-center justify-center rounded bg-white/10 hover:bg-white/25 text-white transition-colors"
            title="Minimize"
            @click="minimized = true"
          >
            <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="text-xs" />
          </button>
        </div>

        <!-- Ringing inbound -->
        <div v-if="state === 'ringing-inbound'" class="p-4">
          <p class="text-sm text-gray-600 mb-1">Incoming call from</p>
          <p class="sensitive text-lg font-semibold text-neutral-900">{{ formatPhone(callerNumber) }}</p>
          <!-- Patient context during ring -->
          <p v-if="matchCount === 1" class="text-sm text-green-700 mb-4">
            <i class="fa-solid fa-user-check mr-1" />
            <a
              class="sensitive underline cursor-pointer hover:text-green-900"
              @click="goToPatient(matchedEntities[0]?.entity_id)"
            >{{ matchedEntities[0]?.entity_name }}</a>
          </p>
          <div v-else-if="matchCount > 1" class="text-sm text-yellow-700 mb-4">
            <p class="mb-1">
              <i class="fa-solid fa-users mr-1" />{{ matchCount }} patients matched
            </p>
            <ul class="ml-5 space-y-0.5">
              <li v-for="entity in matchedEntities" :key="entity.entity_id">
                <a
                  class="sensitive underline cursor-pointer hover:text-yellow-900"
                  @click="goToPatient(entity.entity_id)"
                >{{ entity.entity_name }}</a>
                <span v-if="entity.bene_mbi" class="sensitive text-yellow-600 ml-1 text-xs">{{ entity.bene_mbi }}</span>
              </li>
            </ul>
          </div>
          <p v-else class="text-sm text-gray-500 mb-4">
            <i class="fa-solid fa-user-xmark mr-1" />Unknown caller
          </p>
          <div class="flex gap-2">
            <button class="btn-primary-sm grow" @click="acceptCall">
              <i class="fa-solid fa-phone" />
              Accept
            </button>
            <button class="btn-danger-sm grow" @click="declineCall">
              <i class="fa-solid fa-phone-hangup" />
              Decline
            </button>
          </div>
        </div>

        <!-- Ringing outbound (connecting) -->
        <div v-else-if="state === 'ringing-outbound'" class="p-4 text-center">
          <div class="mb-3">
            <i class="fa-solid fa-phone-arrow-up-right text-2xl text-primary-500 animate-pulse" />
          </div>
          <p class="text-sm text-gray-600 mb-1">Calling</p>
          <p class="sensitive text-lg font-semibold text-neutral-900 mb-4">{{ formatPhone(callerNumber) }}</p>
          <button class="btn-danger-sm w-full" @click="endCall">
            <i class="fa-solid fa-phone-hangup" />
            Cancel
          </button>
        </div>

        <!-- Active call -->
        <div v-else-if="state === 'active'" class="p-4">
          <p class="text-sm text-gray-600 mb-1">Connected</p>
          <p v-if="callerNumber" class="sensitive text-sm font-medium text-neutral-900">{{ formatPhone(callerNumber) }}</p>
          <p class="text-lg font-semibold text-neutral-900 tabular-nums mb-3">{{ formattedDuration }}</p>

          <!-- Engagement linked — show navigation -->
          <div v-if="workId" class="mb-3 p-2 bg-green-50 rounded-lg border border-green-200">
            <p class="text-sm text-green-800 font-medium mb-2">
              <i class="fa-solid fa-user-check mr-1" /><span class="sensitive">{{ entityName }}</span>
            </p>
            <div class="space-y-1.5">
              <button
                v-if="entityId"
                class="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-green-100 transition-colors text-green-800"
                @click="goToPatient(entityId)"
              >
                <i class="fa-solid fa-user mr-1.5 w-4 text-center" />
                View Patient
              </button>
              <button
                class="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-green-100 transition-colors text-green-800"
                @click="goToEngagement"
              >
                <i class="fa-solid fa-clipboard-check mr-1.5 w-4 text-center" />
                Go to Clinical Engagement
              </button>
            </div>
          </div>

          <!-- Creating engagement spinner -->
          <div v-else-if="engagementCreating" class="mb-3 p-3 text-center text-sm text-gray-500">
            <i class="fa-solid fa-spinner fa-spin mr-1" />Creating engagement...
          </div>

          <!-- Engagement creation error -->
          <div v-else-if="engagementError && !workId" class="mb-3 p-3 bg-red-50 rounded-lg border border-red-200">
            <p class="text-sm text-red-700 mb-2">
              <i class="fa-solid fa-circle-exclamation mr-1" />{{ engagementError }}
            </p>
            <div class="flex gap-2">
              <button
                v-if="matchCount === 1"
                class="text-xs text-red-600 hover:text-red-800 font-medium"
                @click="createEngagementForEntity(matchedEntities[0])"
              >
                <i class="fa-solid fa-arrow-rotate-right mr-1" />Retry
              </button>
              <button
                class="text-xs text-gray-500 hover:text-gray-700"
                @click="engagementError = null"
              >
                Dismiss
              </button>
            </div>
          </div>

          <!-- Multi-match: show patient picker -->
          <div v-else-if="matchCount > 1 && !showPatientSearch" class="mb-3">
            <p class="text-xs font-semibold text-gray-500 uppercase mb-2">
              Matched patients
            </p>
            <div class="space-y-2 max-h-48 overflow-y-auto">
              <div
                v-for="entity in matchedEntities"
                :key="entity.entity_id"
                class="px-3 py-2 rounded-lg border border-gray-200 text-sm"
              >
                <div class="flex items-baseline gap-1 mb-1.5">
                  <span class="sensitive font-medium text-neutral-900">{{ entity.entity_name }}</span>
                  <span v-if="entity.bene_mbi" class="sensitive text-gray-500 text-xs">{{ entity.bene_mbi }}</span>
                </div>
                <div class="flex gap-2">
                  <button
                    class="text-xs text-blue-600 hover:text-blue-800"
                    @click="goToPatient(entity.entity_id)"
                  >
                    <i class="fa-solid fa-user mr-1" />View Patient
                  </button>
                  <button
                    class="text-xs text-green-700 hover:text-green-900"
                    @click="selectPatient(entity)"
                  >
                    <i class="fa-solid fa-plus-circle mr-1" />Create Engagement
                  </button>
                </div>
              </div>
            </div>
            <button
              class="mt-2 text-xs text-blue-600 hover:text-blue-800"
              @click="showPatientSearch = true"
            >
              None of these &mdash; search instead
            </button>
          </div>

          <!-- No match or "none of these": show patient search -->
          <div v-else-if="!workId && (matchCount === 0 || showPatientSearch)" class="mb-3">
            <p class="text-xs font-semibold text-gray-500 uppercase mb-2">
              Link call to patient
            </p>

            <!-- Selected patient confirmation -->
            <div v-if="selectedSearchEntity" class="p-2 bg-blue-50 rounded-lg border border-blue-200">
              <div class="flex items-center justify-between mb-2">
                <div class="min-w-0">
                  <span class="sensitive text-sm font-medium text-neutral-900">{{ selectedSearchEntity.name }}</span>
                  <span v-if="selectedSearchEntity.identifiers?.BENE_MBI_ID" class="sensitive text-gray-500 text-xs ml-1">{{ selectedSearchEntity.identifiers.BENE_MBI_ID }}</span>
                </div>
                <button
                  class="text-xs text-gray-400 hover:text-gray-600 shrink-0 ml-2"
                  @click="selectedSearchEntity = null"
                >
                  <i class="fa-solid fa-xmark" />
                </button>
              </div>
              <button
                class="btn-primary-sm w-full text-sm"
                :disabled="engagementCreating"
                @click="confirmSearchSelection"
              >
                <i v-if="engagementCreating" class="fa-solid fa-spinner fa-spin mr-1" />
                <i v-else class="fa-solid fa-link mr-1" />
                {{ engagementCreating ? 'Linking...' : 'Link Call to Patient' }}
              </button>
              <button
                class="w-full text-xs text-gray-500 hover:text-gray-700 mt-1.5 py-1"
                @click="selectedSearchEntity = null"
              >
                <i class="fa-solid fa-arrow-left mr-1" />Back to search
              </button>
            </div>

            <!-- Search input -->
            <template v-else>
              <div class="relative mb-2">
                <input
                  ref="patientSearchInput"
                  v-model="patientSearchQuery"
                  type="text"
                  placeholder="Search by patient name..."
                  class="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  @input="debouncedSearch"
                />
                <i
                  v-if="patientSearchLoading"
                  class="fa-solid fa-circle-notch fa-spin absolute right-3 top-2 text-gray-400 text-sm"
                />
              </div>
              <!-- Skeleton loading -->
              <div v-if="patientSearchLoading" class="space-y-1">
                <div
                  v-for="n in 3"
                  :key="n"
                  class="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100"
                >
                  <div class="w-3 h-3 rounded-full bg-gray-200 animate-pulse shrink-0" />
                  <div class="grow space-y-1">
                    <div class="h-3.5 bg-gray-200 rounded animate-pulse" :style="{ width: (60 + n * 10) + '%' }" />
                  </div>
                </div>
              </div>
              <!-- Results -->
              <div
                v-else-if="patientSearchResults.length"
                class="space-y-1 max-h-32 overflow-y-auto"
              >
                <button
                  v-for="entity in patientSearchResults"
                  :key="entity.id"
                  class="w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-sm"
                  @click="selectedSearchEntity = entity"
                >
                  <i class="fa-solid fa-user text-gray-400 shrink-0 text-xs" />
                  <div class="grow min-w-0">
                    <span class="sensitive font-medium text-neutral-900">{{ entity.name }}</span>
                    <span v-if="entity.identifiers?.BENE_MBI_ID" class="sensitive text-gray-500 ml-1">{{ entity.identifiers.BENE_MBI_ID }}</span>
                  </div>
                </button>
              </div>
              <p
                v-else-if="patientSearchQuery.length >= 2 && !patientSearchLoading && patientSearched"
                class="text-xs text-gray-500 text-center py-2"
              >
                No patients found
              </p>
            </template>
          </div>

          <!-- DTMF Keypad -->
          <div v-if="showKeypad" class="mb-3">
            <div
              v-if="dtmfDisplay"
              class="text-center text-lg font-mono tracking-widest text-neutral-800 mb-2 select-all"
            >{{ dtmfDisplay }}</div>
            <div class="grid grid-cols-3 gap-1.5">
              <button
                v-for="key in dtmfKeys"
                :key="key.digit"
                class="h-11 rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors flex flex-col items-center justify-center select-none"
                @click="sendDtmf(key.digit)"
              >
                <span class="text-base font-semibold text-neutral-900 leading-none">{{ key.digit }}</span>
                <span v-if="key.sub" class="text-[9px] text-gray-400 leading-none mt-0.5 tracking-wider">{{ key.sub }}</span>
              </button>
            </div>
          </div>

          <!-- Call controls -->
          <div class="flex gap-2">
            <button
              class="grow rounded px-3 py-1.5 text-sm font-semibold transition-colors"
              :class="muted ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              @click="toggleMute"
            >
              <i :class="muted ? 'fa-solid fa-microphone-slash' : 'fa-solid fa-microphone'" />
              {{ muted ? 'Unmute' : 'Mute' }}
            </button>
            <button
              class="rounded px-3 py-1.5 text-sm font-semibold transition-colors"
              :class="showKeypad ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              title="Keypad"
              @click="showKeypad = !showKeypad"
            >
              <i class="fa-solid fa-keyboard" />
            </button>
            <button class="btn-danger-sm grow" @click="endCall">
              <i class="fa-solid fa-phone-hangup" />
              End
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { markRaw, toRaw } from 'vue'
import axios from 'axios'
import { twilioDevice } from '@/services/TwilioDeviceService'
import { createPhoneEngagement } from '@/services/api/voiceApi'

export default {
  name: 'PhoneCallFloater',
  data() {
    return {
      state: 'idle',
      minimized: false,
      muted: false,
      callerNumber: '',
      durationSeconds: 0,
      /** @type {import('@twilio/voice-sdk').Call | null} */
      incomingCall: null,
      durationTimer: null,
      // Engagement context
      callSid: null,
      matchCount: 0,
      matchedEntities: [],
      workId: null,
      entityId: null,
      entityName: null,
      engagementCreating: false,
      engagementError: null,
      // Patient search
      selectedSearchEntity: null,
      showPatientSearch: false,
      patientSearchQuery: '',
      patientSearchResults: [],
      patientSearchLoading: false,
      patientSearched: false,
      // Cross-tab
      crossTabContext: null,
      // Keypad
      showKeypad: false,
      dtmfDisplay: '',
      dtmfKeys: [
        { digit: '1', sub: '' },
        { digit: '2', sub: 'ABC' },
        { digit: '3', sub: 'DEF' },
        { digit: '4', sub: 'GHI' },
        { digit: '5', sub: 'JKL' },
        { digit: '6', sub: 'MNO' },
        { digit: '7', sub: 'PQRS' },
        { digit: '8', sub: 'TUV' },
        { digit: '9', sub: 'WXYZ' },
        { digit: '*', sub: '' },
        { digit: '0', sub: '+' },
        { digit: '#', sub: '' },
      ],
    }
  },
  computed: {
    headerLabel() {
      if (this.state === 'ringing-inbound') return 'Incoming Call'
      if (this.state === 'ringing-outbound') return 'Connecting...'
      return 'Phone Call'
    },
    formattedDuration() {
      const m = Math.floor(this.durationSeconds / 60)
      const s = this.durationSeconds % 60
      return `${m}:${String(s).padStart(2, '0')}`
    },
    onMatchingEngagement() {
      const route = this.$route
      return (
        route.name === 'ClinicalEngagement' &&
        this.workId &&
        String(route.params.workId) === String(this.workId)
      )
    },
  },
  watch: {
    onMatchingEngagement(onPage) {
      if (onPage && this.state === 'active') {
        this.minimized = true
      }
    },
  },
  created() {
    this.searchTimer = null
    this._searchAbortController = null
  },
  mounted() {
    twilioDevice.on('incoming', this.onIncoming)
    twilioDevice.on('callConnecting', this.onConnecting)
    twilioDevice.on('callConnected', this.onConnected)
    twilioDevice.on('callRinging', this.onRinging)
    twilioDevice.on('callDisconnected', this.onDisconnected)
    twilioDevice.on('callError', this.onDisconnected)
    twilioDevice.on('muteChanged', this.onMuteChanged)
    twilioDevice.on('crossTabCall', this.onCrossTabCall)
    this._onWindowFocus = () => this._stopFlash()
    this._onWindowBlur = () => {
      if (this.state === 'active') this._flashTitle()
    }
    window.addEventListener('focus', this._onWindowFocus)
    window.addEventListener('blur', this._onWindowBlur)
  },
  beforeUnmount() {
    twilioDevice.off('incoming', this.onIncoming)
    twilioDevice.off('callConnecting', this.onConnecting)
    twilioDevice.off('callConnected', this.onConnected)
    twilioDevice.off('callRinging', this.onRinging)
    twilioDevice.off('callDisconnected', this.onDisconnected)
    twilioDevice.off('callError', this.onDisconnected)
    twilioDevice.off('muteChanged', this.onMuteChanged)
    twilioDevice.off('crossTabCall', this.onCrossTabCall)
    window.removeEventListener('focus', this._onWindowFocus)
    window.removeEventListener('blur', this._onWindowBlur)
    this._stopTimer()
    this._stopFlash()
    if (this.searchTimer) clearTimeout(this.searchTimer)
    if (this._searchAbortController) this._searchAbortController.abort()
  },
  methods: {
    onIncoming(payload) {
      this.incomingCall = markRaw(payload.call)
      this.callerNumber = payload.fromNumber || payload.call.parameters?.From || 'Unknown'
      this.callSid = payload.callSid
      this.matchCount = payload.matchCount
      this.matchedEntities = payload.matchedEntities
      this.state = 'ringing-inbound'
      this.minimized = false
    },
    onConnecting(call) {
      this.callerNumber = call.customParameters?.get('to_phone_number') || ''
      this.state = 'ringing-outbound'
      this.minimized = false

      const ctx = twilioDevice.currentCallContext
      if (ctx?.workId) {
        this.workId = ctx.workId
        this.entityId = ctx.entityId
        this.entityName = ctx.entityName
        this.matchCount = -1
      }
    },
    onRinging() {
      if (this.state === 'idle') {
        this.state = 'ringing-outbound'
      }
    },
    onConnected() {
      this.state = 'active'
      this.durationSeconds = 0
      this.muted = false
      this._startTimer()
      if (!document.hasFocus()) this._flashTitle()
      if (this.onMatchingEngagement) {
        this.minimized = true
      }
    },
    onCrossTabCall(context) {
      this.crossTabContext = context
    },
    focusCallTab() {
      twilioDevice.requestFocusCallTab()
    },
    _flashTitle() {
      if (this._flashTimer) return
      this._flashBaseTitle = document.title
      let on = true
      this._flashTimer = setInterval(() => {
        document.title = on ? '\u{1F4DE} PHONE CALL ACTIVE' : this._flashBaseTitle
        on = !on
      }, 800)
    },
    _stopFlash() {
      if (this._flashTimer) {
        clearInterval(this._flashTimer)
        this._flashTimer = null
        document.title = this._flashBaseTitle || document.title
        this._flashBaseTitle = null
      }
    },
    onDisconnected() {
      this._stopTimer()
      if (this._searchAbortController) {
        this._searchAbortController.abort()
        this._searchAbortController = null
      }
      this.state = 'idle'
      this.minimized = false
      this.muted = false
      this.incomingCall = null
      this.callerNumber = ''
      this.durationSeconds = 0
      this.callSid = null
      this.matchCount = 0
      this.matchedEntities = []
      this.workId = null
      this.entityId = null
      this.entityName = null
      this.engagementCreating = false
      this.engagementError = null
      this.selectedSearchEntity = null
      this.showPatientSearch = false
      this.patientSearchQuery = ''
      this.patientSearchResults = []
      this.patientSearched = false
      this.crossTabContext = null
      this.showKeypad = false
      this.dtmfDisplay = ''
    },
    async acceptCall() {
      if (!this.incomingCall) return
      twilioDevice.acceptIncoming(toRaw(this.incomingCall))
      this.incomingCall = null

      if (this.matchCount === 1) {
        await this.createEngagementForEntity(this.matchedEntities[0])
      }
    },
    declineCall() {
      if (this.incomingCall) {
        twilioDevice.rejectIncoming(toRaw(this.incomingCall))
        this.incomingCall = null
      }
    },
    async createEngagementForEntity(entity) {
      this.engagementCreating = true
      this.engagementError = null
      try {
        const response = await createPhoneEngagement({
          entityId: entity.entity_id,
          callSid: this.callSid,
        })
        this.workId = response.work_id
        this.entityId = entity.entity_id
        this.entityName = entity.entity_name
        twilioDevice.linkEngagement({
          workId: this.workId,
          entityId: entity.entity_id,
          entityName: entity.entity_name,
        })
      } catch (err) {
        console.error('[PhoneCallFloater] engagement creation failed:', err?.message)
        this.engagementError =
          'Could not create engagement. Please try again or link manually.'
      } finally {
        this.engagementCreating = false
      }
    },
    async selectPatient(entity) {
      await this.createEngagementForEntity(entity)
    },
    async confirmSearchSelection() {
      if (!this.selectedSearchEntity) return
      await this.createEngagementForEntity({
        entity_id: this.selectedSearchEntity.id,
        entity_name: this.selectedSearchEntity.name,
      })
      this.selectedSearchEntity = null
    },
    debouncedSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.searchPatients(), 300)
    },
    async searchPatients() {
      const query = this.patientSearchQuery.trim()
      if (query.length < 2) return
      if (this._searchAbortController) this._searchAbortController.abort()
      this._searchAbortController = new AbortController()
      this.patientSearchLoading = true
      try {
        const { data } = await axios.get('/api/entities/', {
          params: { entity_type_name: 'Patient', name: query },
          signal: this._searchAbortController.signal,
        })
        const raw = Array.isArray(data) ? data : (data.results ?? [])
        const seen = new Set()
        this.patientSearchResults = raw
          .filter((e) => {
            const key = e.identifiers?.BENE_MBI_ID ?? `id:${e.id}`
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })
          .map((e) => ({
            id: e.id,
            name: e.name,
            identifiers: e.identifiers || {},
          }))
        this.patientSearched = true
      } catch (e) {
        if (axios.isCancel(e)) return
        console.error('[PhoneCallFloater] patient search failed:', e?.message)
      } finally {
        this.patientSearchLoading = false
      }
    },
    goToPatient(entityId) {
      this.$router.push({
        name: 'CareManagement-PatientDetail',
        params: { patientId: entityId },
      })
    },
    goToEngagement() {
      this.$router.push({
        name: 'ClinicalEngagement',
        params: { workId: this.workId },
      })
    },
    onMuteChanged(muted) {
      this.muted = muted
    },
    toggleMute() {
      this.muted = twilioDevice.toggleMute()
    },
    formatPhone(phone) {
      if (!phone) return 'Unknown'
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      return phone
    },
    sendDtmf(digit) {
      twilioDevice.sendDigits(digit)
      this.dtmfDisplay += digit
    },
    endCall() {
      twilioDevice.disconnect()
    },
    _startTimer() {
      this._stopTimer()
      this.durationTimer = setInterval(() => {
        this.durationSeconds++
      }, 1000)
    },
    _stopTimer() {
      if (this.durationTimer) {
        clearInterval(this.durationTimer)
        this.durationTimer = null
      }
    },
  },
}
</script>

<style scoped>
.floater-slide-enter-active,
.floater-slide-leave-active {
  transition: all 0.3s ease;
}
.floater-slide-enter-from,
.floater-slide-leave-to {
  opacity: 0;
  transform: translateY(16px);
}
</style>
